/* lbf-frozen-moq.js v1.14 — frozen MOQ rescue card + gate. Replaces theme.liquid frozen_min_notice_v4. Spec: frozen-moq-both-surfaces-mock.html + DECISION_LOG revisions 1-5.
 * v1.1 (2026-07-09 bug-fix pass):
 *   - Quick-add strip -> responsive snap CAROUSEL: exactly 2 cards/view <750px,
 *     4 cards/view >=750px, circular prev/next arrows that page by one full
 *     container width and hide at each end (or entirely when all cards fit).
 *   - Best-sellers-first pool: frozen-family-meals sortOrder is ALPHA_ASC (not
 *     BEST_SELLING, checked via Admin API 2026-07-09), so seed from
 *     /recommendations/products.json for the first frozen line (popularity-
 *     weighted), then top up from the collection JSON chain. Sold-out products
 *     skipped.
 *   - Green removal banner now TRACKS the live bottom edge of Dawn's
 *     re-appearing sticky header (rAF loop while visible) and keeps z-index
 *     below the header's, so it never hides behind or covers navigation.
 *   - contain:inline-size on the helper card so it can never widen the cart
 *     <table> (paired with the display:block row fix in main-cart-items.liquid).
 */
/*
 * The 5 rules:
 *   1) 0 frozen  -> nothing shown.
 *   2) 3+ frozen OR cc_solo_ok in cart -> normal checkout, NO MOQ messaging
 *      anywhere (no waiver callout, no "minimum met").
 *   3) 1-2 frozen WITH RTE -> helper card, primary button by profit rule:
 *      addPrimary = (need === 1) || (rteValueCents < CROSS [RM55 default]).
 *   4) 1-2 frozen ONLY -> helper card, quick-adds primary, no remove path.
 *   5) Nudges focus on the next action; "Why frozen needs 3 packs?" opens an
 *      explainer popup that repeats the same action buttons.
 *
 * While blocked: checkout GREYED + disabled (never display:none), subtotal +
 * shipping stay visible (cart-footer-fix.js preloads from lbf-ship-state),
 * free-ship coaching + cold-chain chip hidden via .lbf-moq-blocked CSS,
 * sticky footer strip "(i) Frozen: X of 3 minimum [segs] Resolve ^" outside .js-contents.
 *
 * IMPORTANT copy constraint: cart-footer-fix.js hides totals when page text
 * matches /add\s+\d+\s+more\s+frozen\s+item/i or
 * /minimum\s+\d+\s+frozen\s+items?\s+required/i — all copy here says
 * "frozen dish(es)" / "Frozen: X of 3 minimum" and must stay that way.
 *
 * v1.2 (2026-07-09):
 *   - Quick-add/hero cards now show the same pax/weight/time USP badges as the
 *     collection page (scraped from real card markup, see loadUspMap()).
 *   - Region-picker fix: .lbf-moq-blocked no longer display:none's the whole
 *     .lbf-status-box, which was hiding the only "Different region?" trigger
 *     once a region had already been picked. See injectCSS().
 *
 * v1.3 (2026-07-09):
 *   - REMOVE-primary free-ship subtext now reads "West Msia Free Shipping" in
 *     the established shipping green (#2D8A2D, matches .lbf-free elsewhere)
 *     instead of "ships FREE" in whatever color the button's is-primary/
 *     is-secondary state happened to use. Dropped "(undo-able)" from both RTE
 *     subtext variants -- redundant given the removal banner's explicit UNDO.
 *   - "browse frozen best-sellers" -> "Browse Frozen Best Sellers" (title case).
 *
 * v1.4 (2026-07-09, Carl's 4-screenshot copy pass):
 *   - Dropped the damp-packaging disclosure line from the why-popup entirely
 *     (Carl X'd it out live) -- the popup now stops at the RTE-checks-out-
 *     separately point.
 *   - Removed every trailing "->" from button/prose copy (Carl: "remove all
 *     the -> in all copy"). The up-arrow in the sticky strip's "Resolve ^" is
 *     a different glyph doing a different job (scroll-to, not "next step")
 *     and was left alone.
 *   - All .lbf-moq-btn / .lbf-moq-hero-add labels now render UPPERCASE via
 *     CSS text-transform (Carl: "Text on button should be caps on all time").
 *     JS strings stay natural-case in source for readability; CSS does the
 *     visual transform, so it also automatically uppercases small-print
 *     subtext (e.g. "West Msia Free Shipping" -> "WEST MSIA FREE SHIPPING").
 *   - ADD button simplified: dropped the redundant "Add N more frozen
 *     dish(es)" headline (the card's own <h3> already states the count) --
 *     the button now always just reads "Browse Frozen Best Sellers", in every
 *     context that offers it (mixed-cart ADD, hero fallback, frozen-only
 *     card, why-popup). Free-ship flip nudge, when it applies, now shows as
 *     green subtext under that single label instead of replacing it.
 *   - RTE button subtext simplified to a single static line, "Remove Frozen
 *     Dishes" (Carl: "this is the only line that should be under 'Checkout
 *     Ready-To-Eat First'"), replacing the old dynamic "N items · RMx.xx ·
 *     frozen removed" / "West Msia Free Shipping" text -- which also
 *     sidesteps a real confusion Carl hit where the RTE line's free-ship
 *     flip didn't visibly match what he expected from the retort value alone
 *     (that math lived only in the button; it's simpler and less error-prone
 *     to let the cart's own shipping-line summary be the one source of truth
 *     for free-shipping status, and have this button just describe the
 *     ACTION it takes). rteFreeShipBenefit() removed as dead code.
 *
 * v1.5 (2026-07-10, Carl's simplification pass — button count + row layout):
 *   - Mixed-cart and hero cards now show exactly ONE action button by
 *     default (whichever the profit-aware rule favors); the other action
 *     moved into the existing "why" popup instead of sitting as a second
 *     permanent button. The popup already explained the reasoning and
 *     repeated both actions — now it repeats only the one not already
 *     visible in the card. No change to which action is favored.
 *   - Quick-add cards switched from an 8-card portrait snap-carousel to a
 *     capped 2-row compact list (image left, title/USP/price/ADD in a
 *     row) -- carousel arrows and paging removed entirely. Dropped the
 *     redundant "FROZEN" badge on these rows (every item here is frozen
 *     by definition).
 *
 * v1.6 (2026-07-10, further simplification pass):
 *   - REMOVE-favored and frozen-only cards no longer restate the frozen
 *     count in a separate sub-paragraph -- that fact already persists in
 *     the sticky footer strip. Frozen-only headline now folds the "add N
 *     more" number directly into its single line. ADD-favored and hero
 *     branches unchanged (already lean / not duplicative).
 *
 * v1.7 (2026-07-10, remove-and-go): "Checkout Ready-to-Eat first" no longer
 *   shows a "Remove Frozen Dishes" subtext -- the green UNDO banner already
 *   announces the same fact right after the click, with a reversal option,
 *   so the pre-emptive explainer was redundant. Removal now auto-scrolls to
 *   the checkout button afterward (mirrors scrollToHelper() in the other
 *   direction) instead of leaving the customer to find it themselves.
 *
 * v1.8 (2026-07-10, popup reinforcement + 6-SKU strip):
 *   - The why-popup now includes the priority action first, then the alternate
 *     action, so the favored behavior is reinforced inside the explanation.
 *   - Quick-add rows now render as one horizontal scrollable strip capped at
 *     six SKUs, keeping selection easy while using less vertical space.
 *
 * v1.9 (2026-07-10, chooser scale + RTE free-ship incentive):
 *   - Horizontal quick-add cards are larger, closer to cart recommendation row
 *     scale, so product title/image/price are easier to judge before adding.
 *   - "Checkout Ready-to-Eat first" shows green "West Msia Free Shipping"
 *     subtext when the RTE subtotal alone reaches the West Malaysia dry free-
 *     shipping threshold, making the remove-frozen path's benefit explicit.
 *
 * v1.10 (2026-07-10, visual polish):
 *   - Helper card/chooser borders use brand gold (#be995c). Quick-add product
 *     rows now share a stable height, keep USP meta on one row, and style sale
 *     pricing with strikethrough original + red current price.
 *   - Frozen header drops the cold-chain wording after MOQ is satisfied; RTE
 *     section header changes from grey to the same gold treatment.
 *
 * v1.11 (2026-07-10, header simplification):
 *   - Group headers now show only "FROZEN" or "READY-TO-EAT"; the snowflake,
 *     cold-chain suffix, RTE sublabel, and fulfilled-MOQ counter text are
 *     removed to reduce header confusion. Progress dots remain only while
 *     the frozen MOQ is blocked.
 *
 * v1.12 (2026-07-10, quick-add peek tune):
 *   - Horizontal quick-add rows now leave a visible peek of the next frozen
 *     product card while preserving the full one-row USP badges on the active
 *     row. The helper/row border remains the 1px brand-gold treatment.
 *   - Desktop gets explicit left/right scroll arrows for the horizontal row
 *     strip; mobile remains swipe/peek only.
 *
 * v1.13 (2026-07-16, resolution-path analytics):
 *   - Rescue-card add actions set cart attribute lbf_moq_resolution=add.
 *   - Remove-and-checkout merges lbf_moq_resolution=remove into its existing
 *     /cart/update.js request, so removing frozen lines remains one mutation.
 *   - Standard cart quantity/remove controls remain untagged by design.
 *
 * v1.14 (2026-07-16, confirmed add resolution):
 *   - Browse now opens the canonical all-products Frozen filter and records
 *     only a one-use session intent. It writes no cart attribute on click.
 *   - Returning to cart consumes that intent and writes add only when the
 *     server cart actually contains 3+ frozen packs.
 *   - Rescue quick-add writes add only after cart/add.js succeeds and the
 *     resulting server cart reaches 3+ frozen packs; 1 -> 2 remains untagged.
 */
(function () {
  'use strict';
  if (window.__lbfFrozenMoq) return;
  window.__lbfFrozenMoq = true;
  if (window.location.pathname.indexOf('/cart') === -1) return;

  var MOQ = 3;
  var LBFCFG = window.LBF_SHIPPING_CONFIG || {};
  var CROSS = LBFCFG.CROSS != null ? LBFCFG.CROSS : 5500; /* RM55 default (theme setting lbf_moq_crossover) */
  var KLSEL_REMOVE_CROSS = LBFCFG.KLSEL_REMOVE_CROSS != null ? LBFCFG.KLSEL_REMOVE_CROSS : 12000;
  var TDRY = LBFCFG.TDRY != null ? LBFCFG.TDRY : 15000;
  var ADD_URL = '/collections/all?sort_by=best-selling&filter.p.product_type=Frozen';
  var FROZEN_HANDLES = ['frozen-family-meals'];
  var RESOLUTION_ATTRIBUTE = 'lbf_moq_resolution';
  var BROWSE_INTENT_KEY = 'lbf_moq_add_browse_intent';

  var cartCache = null;       /* last /cart.js payload */
  var cartFetching = false;
  var quickPool = null;       /* frozen products for the quick-add strip */
  var quickLoading = false;
  var uspByHandle = null;     /* handle -> real collection-card .lbf-card-meta outerHTML (pax/weight/time) */
  var uspLoading = false;
  var removedBackup = null;   /* [{id: variantId, quantity}] for UNDO */
  var currentBlocked = false;
  var lastBeaconKey = '';
  var writing = false;        /* true while we mutate DOM (observer ignores) */
  var renderTimer = null;
  var bannerTimer = null;
  var publishedPrimaryPath = null;
  var publishedPrimaryKey = null;

  /* ---------------- utils ---------------- */

  function root() { return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; }
  function fmt(cents) { return 'RM' + (Number(cents || 0) / 100).toFixed(2); }
  function escapeHTML(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function priceToCents(p) {
    if (typeof p === 'number') return p >= 1000 || p % 1 === 0 ? p : Math.round(p * 100);
    var f = parseFloat(p);
    return isNaN(f) ? 0 : Math.round(f * 100);
  }
  function beginWrite() { writing = true; }
  function endWrite() { setTimeout(function () { writing = false; }, 0); }
  function beacon(event, data) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: event };
      for (var k in data) { if (data.hasOwnProperty(k)) payload[k] = data[k]; }
      window.dataLayer.push(payload);
      if (window.console && console.log) console.log('[lbf-frozen-moq]', event, data);
    } catch (e) {}
  }

  function footerSection() {
    return document.querySelector('[id$="__cart-footer"]') || document.getElementById('main-cart-footer');
  }
  function itemsSection() { return document.getElementById('main-cart-items'); }
  function getCheckoutButtons() {
    return Array.prototype.slice.call(
      document.querySelectorAll('button[name="checkout"], input[name="checkout"], a[href^="/checkout"]')
    );
  }

  /* ---------------- cart state (DOM = MOQ truth, /cart.js = money truth) ---------------- */

  function computeState() {
    var rows = Array.prototype.slice.call(document.querySelectorAll('[data-frozen="1"]'));
    /* FIX A (2026-09-18): the bypass used to be a CART-WIDE querySelector, so one
       cc_solo_ok row disarmed the guard for every other frozen line. The row markup
       already carries data-cc-bypass per line (lbf-cart-item-row.liquid), so read it
       per row: exempt units are excluded from the minimum instead of cancelling it. */
    var bypass = !!document.querySelector('[data-cc-bypass="1"]');
    var fqAll = 0;   // every frozen unit
    var fqMoq = 0;   // NON-exempt frozen units - the only ones the minimum applies to
    rows.forEach(function (row) {
      var input = row.querySelector('input[name^="updates"]') || row.querySelector('input.quantity__input');
      var c = input ? parseInt(input.value, 10) : 1;
      if (isNaN(c)) c = 1;
      fqAll += c;
      if (row.getAttribute('data-cc-bypass') !== '1') fqMoq += c;
    });
    var hasFrozen = rows.length > 0;
    return {
      hasFrozen: hasFrozen,
      bypass: bypass,
      fqAll: fqAll,
      /* fq/need drive the "X of 3" dots and the "add N more" copy, so they must
         report the NON-exempt count or the rescue card asks for the wrong number. */
      fq: fqMoq,
      need: Math.max(0, MOQ - fqMoq),
      blocked: fqMoq > 0 && fqMoq < MOQ
    };
  }

  function fetchCart() {
    cartFetching = true;
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { cartFetching = false; if (c) cartCache = c; return cartCache; })
      .catch(function () { cartFetching = false; return null; });
  }

  function setResolution(path) {
    var attributes = {};
    attributes[RESOLUTION_ATTRIBUTE] = path;
    return fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (r) {
      if (!r.ok) throw new Error('Unable to set MOQ resolution');
      return r.json();
    });
  }

  function frozenQtyFromCart(cart) {
    var qty = 0;
    ((cart && cart.items) || []).forEach(function (it) {
      if (String(it.product_type || '').toLowerCase() === 'frozen') qty += Number(it.quantity || 0);
    });
    return qty;
  }

  function markBrowseIntent() {
    try { sessionStorage.setItem(BROWSE_INTENT_KEY, '1'); } catch (e) {}
  }

  function consumeBrowseIntent(cart) {
    var pending = false;
    try {
      pending = sessionStorage.getItem(BROWSE_INTENT_KEY) === '1';
      if (pending) sessionStorage.removeItem(BROWSE_INTENT_KEY);
    } catch (e) {}
    if (!pending || frozenQtyFromCart(cart) < MOQ) return Promise.resolve(false);
    return setResolution('add').then(function (updatedCart) {
      cartCache = updatedCart;
      return true;
    }).catch(function () { return false; });
  }

  function rteInfo() {
    var count = 0, cents = 0;
    (((cartCache || {}).items) || []).forEach(function (it) {
      var t = String(it.product_type || '').toLowerCase();
      if (t !== 'frozen') { count += 1; cents += it.final_line_price || 0; }
    });
    return { count: count, cents: cents };
  }

  function shipState() {
    try {
      var v = localStorage.getItem('lbf-ship-state');
      return (v === 'kl-sel' || v === 'wm') ? v : null;
    } catch (e) { return null; }
  }

  function primaryPathFor(st) {
    if (!st || !st.blocked) return '';
    var rte = rteInfo();
    if (rte.count <= 0) return 'add';
    if (st.need === 1) {
      return (shipState() === 'kl-sel' && rte.cents >= KLSEL_REMOVE_CROSS) ? 'remove' : 'add';
    }
    return rte.cents >= CROSS ? 'remove' : 'add';
  }

  function isAddPrimary(st) {
    return primaryPathFor(st) !== 'remove';
  }

  function rteFreeShipUnlocked(rte) {
    return rte && rte.count > 0 && rte.cents >= TDRY;
  }

  function publishPrimaryPath(st) {
    var path = (cartCache && st && st.blocked) ? primaryPathFor(st) : '';
    var rte = rteInfo();
    var key = [
      path,
      shipState() || '',
      st ? st.fq : 0,
      st ? st.need : 0,
      rte.cents,
      (cartCache && cartCache.total_price) || 0
    ].join('|');
    if (key === publishedPrimaryKey) return;
    publishedPrimaryKey = key;
    publishedPrimaryPath = path;
    try { window.LBF_MOQ_PRIMARY_PATH = path; } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('lbf:moq-primary-path', {
        detail: { path: path, state: shipState(), frozen_qty: st ? st.fq : 0, need: st ? st.need : 0 }
      }));
    } catch (e) {}
  }

  function useCartPayload(payload) {
    var next = payload && (payload.cartData || payload.cart || payload);
    if (!next || !next.items) return false;
    cartCache = next;
    scheduleRender(0);
    return true;
  }

  /* ---------------- CSS ---------------- */

  function injectCSS() {
    if (document.getElementById('lbf-moq-css')) return;
    var css = ''
      /* cart-footer-fix applies its 100vw full-bleed trick to BOTH the section
         wrapper and the inner .cart__footer; the doubled application overshoots
         the viewport by the scrollbar width on classic-scrollbar browsers and
         creates ~8px horizontal page overflow. The outer wrapper already
         handles the bleed, so pin the inner one to its parent. */
      + '@media(max-width:749px){[id$="__cart-footer"]{padding-left:0!important;padding-right:0!important}[id$="__cart-footer"] .cart__footer{width:100%!important;max-width:100%!important;margin-left:0!important;margin-right:0!important}}'
      /* while blocked: hold back free-ship coaching + cold-chain chip (keep .lbf-state-row pills
         visible). v1.2 (2026-07-09): don't display:none the WHOLE .lbf-status-box once a region
         is already picked -- cart-footer-fix.js puts the ONLY "Different region?" trigger inside
         that same box, so hiding it entirely left customers with no way to change region while
         blocked. Hide just the icon/coaching text; keep the change-region link reachable. */
      + '.lbf-moq-blocked .lbf-status-box{background:none!important;padding:2px 0!important;margin-top:6px!important}'
      + '.lbf-moq-blocked .lbf-status-icon,.lbf-moq-blocked .lbf-status-text{display:none!important}'
      + '.lbf-moq-blocked .lbf-cold-chain-note{display:none!important}'
      + '.lbf-moq-blocked .cart__ctas button[name="checkout"],.lbf-moq-blocked .cart__ctas input[name="checkout"]{opacity:.5;cursor:not-allowed}'
      /* helper card */
      + '.lbf-moq-card{background:#FFF4E5;border:1px solid #be995c;border-radius:12px;padding:12px;margin:10px 0 4px;font-size:1.3rem;line-height:1.4;box-sizing:border-box;max-width:100%;min-width:0;contain:inline-size}'
      + '.lbf-moq-card h3{font-size:13px;font-weight:800;color:#8B4500;line-height:1.35;margin:0}'
      + '.lbf-moq-card .lbf-moq-sub{font-size:11px;color:#8B4500;opacity:.85;margin:2px 0 0}'
      + '.lbf-moq-btnrow{display:flex;flex-direction:column;gap:8px;margin-top:10px}'
      + '.lbf-moq-btn{width:100%;border-radius:999px;padding:11px 10px;font-size:12.5px;font-weight:800;cursor:pointer;line-height:1.3;font-family:inherit;text-transform:uppercase}'
      + '.lbf-moq-btn.is-primary{background:#FFD400;border:0;color:#121212}'
      + '.lbf-moq-btn.is-secondary{background:#fff;border:1.5px solid #ddd;color:#444}'
      + '.lbf-moq-btn.is-secondary:hover{border-color:#999}'
      + '.lbf-moq-btn small{display:block;font-weight:600;font-size:10px}'
      + '.lbf-moq-btn.is-primary small{color:#6b5900}'
      + '.lbf-moq-btn.is-secondary small{color:#2D8A2D}'
      + '.lbf-ship-free{color:#2D8A2D!important;font-weight:800}'
      + '.lbf-moq-btn.lbf-moq-browse{margin-top:8px}'
      + '.lbf-moq-why{display:block;margin:9px auto 0;background:none;border:0;color:#8B4500;text-decoration:underline;text-underline-offset:2px;font-size:11px;font-weight:600;cursor:pointer;padding:0;font-family:inherit}'
      /* S4/S5-v1 hero card (need===1, playbook: "strongest visual treatment
         in the system... single suggested pack, one-tap add") */
      + '.lbf-moq-hero{border:1px solid #be995c;box-shadow:0 2px 10px rgba(190,153,92,.22)}'
      + '.lbf-moq-hero h3{font-size:15px}'
      + '.lbf-moq-sub--gold{color:#8B6B00;font-weight:700;opacity:1}'
      + '.lbf-moq-hero-pick{display:flex;align-items:center;gap:10px;margin-top:8px;background:#fff;border:1px solid #be995c;border-radius:12px;padding:9px;min-height:92px;min-width:0;max-width:100%;box-sizing:border-box}'
      + '.lbf-moq-hero-pick img{width:64px;height:64px;border-radius:8px;object-fit:cover;flex-shrink:0;display:block}'
      + '.lbf-moq-hero-info{flex:1;min-width:0}'
      + '.lbf-moq-hero-info .t{font-size:12px;font-weight:700;color:#333;line-height:1.3;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}'
      + '.lbf-moq-hero-info .p{font-size:12px;font-weight:800;color:#121212;margin-top:2px;display:flex;gap:4px;align-items:baseline;flex-wrap:wrap}'
      + '.lbf-moq-hero-info .p .orig{font-size:10.5px;font-weight:400;color:#aaa;text-decoration:line-through}'
      + '.lbf-moq-hero-info .p .cur{color:#c1121f}'
      + '.lbf-moq-hero-add{flex-shrink:0;background:#FFD400;border:0;border-radius:999px;padding:10px 18px;font-size:12.5px;font-weight:800;cursor:pointer;color:#121212;font-family:inherit;text-transform:uppercase}'
      + '.lbf-moq-hero-add[disabled]{opacity:.6;cursor:default}'
      + '.lbf-moq-hero-alt{margin-top:10px;min-width:0;max-width:100%;contain:inline-size}'
      + '.lbf-moq-hero-alt-label{font-size:10.5px;font-weight:700;color:#8B6B00;opacity:.75;margin:0 0 4px}'
      + '.lbf-moq-hero-info .lbf-card-meta{margin:4px 0 3px;gap:3px;flex-wrap:nowrap;justify-content:flex-start;overflow:visible;white-space:nowrap;width:auto}'
      + '.lbf-moq-hero-info .lbf-card-meta>*{flex:0 0 auto}'
      + '.lbf-moq-hero-info .lbf-card-meta__item{font-size:7.5px;padding:1px 4px;gap:1px}'
      + '.lbf-moq-hero-info .lbf-card-meta__item svg{width:7px;height:7px}'
      /* quick-add rows */
      + '.lbf-moq-carousel{position:relative;min-width:0;max-width:100%}'
      + '.lbf-moq-rows{display:flex;flex-direction:row;gap:8px;margin-top:10px;min-width:0;max-width:100%;contain:inline-size;overflow-x:auto;padding:2px 0 4px;scroll-snap-type:x mandatory;scrollbar-width:none;-webkit-overflow-scrolling:touch}'
      + '.lbf-moq-rows::-webkit-scrollbar{display:none}'
      + '.lbf-moq-row{display:flex;align-items:center;gap:8px;background:#fff;border:1px solid #be995c;border-radius:10px;padding:8px;min-height:92px;min-width:0;flex:0 0 min(92%,360px);scroll-snap-align:start;box-sizing:border-box}'
      + '.lbf-moq-row img{width:60px;height:60px;border-radius:8px;object-fit:cover;flex-shrink:0;display:block}'
      + '.lbf-moq-row-info{flex:1;min-width:0}'
      + '.lbf-moq-row-info .t{font-size:12.5px;font-weight:700;color:#333;line-height:1.28;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}'
      + '.lbf-moq-row-info .p{font-size:12px;font-weight:800;color:#121212;margin-top:3px;display:flex;gap:4px;align-items:baseline;flex-wrap:wrap}'
      + '.lbf-moq-row-info .p .orig{font-size:10px;font-weight:400;color:#aaa;text-decoration:line-through}'
      + '.lbf-moq-row-info .p .cur{color:#c1121f}'
      + '.lbf-moq-row-info .lbf-card-meta{margin:0 0 3px;gap:3px;flex-wrap:nowrap;justify-content:flex-start;overflow:visible;white-space:nowrap;width:auto}'
      + '.lbf-moq-row-info .lbf-card-meta>*{flex:0 0 auto}'
      + '.lbf-moq-row-info .lbf-card-meta__item{font-size:8px;padding:1px 4px;gap:1px}'
      + '.lbf-moq-row-info .lbf-card-meta__item svg{width:8px;height:8px}'
      + '.lbf-moq-row button{flex-shrink:0;background:#FFD400;border:0;border-radius:999px;padding:9px 15px;font-size:11.5px;font-weight:800;cursor:pointer;color:#121212;font-family:inherit}'
      + '.lbf-moq-row button[disabled]{opacity:.6;cursor:default}'
      + '.lbf-moq-arrow{display:none}'
      + '@media(min-width:750px){.lbf-moq-carousel{padding:0 36px}.lbf-moq-carousel .lbf-moq-rows{margin-top:10px}.lbf-moq-row{flex-basis:min(33.333%,410px)}.lbf-moq-arrow{position:absolute;top:50%;transform:translateY(-50%);display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:999px;border:1px solid #be995c;background:#fff;color:#8B4500;box-shadow:0 2px 8px rgba(0,0,0,.12);cursor:pointer;z-index:2;padding:0}.lbf-moq-arrow[hidden]{display:none}.lbf-moq-arrow svg{width:8px;height:12px;display:block}.lbf-moq-arrow--prev{left:0}.lbf-moq-arrow--next{right:0}}'
      /* sticky footer strip */
      + '.lbf-moq-strip{display:flex;align-items:center;gap:8px;background:#EAF2FB;color:#1A5490;border-radius:8px;padding:6px 10px;font-size:11px;font-weight:600;margin:6px 0 0!important}'
      + '.lbf-moq-strip .segs{display:flex;gap:2px;width:44px;flex-shrink:0}'
      + '.lbf-moq-strip .segs span{flex:1;height:4px;border-radius:2px;background:#c9dcf2}'
      + '.lbf-moq-strip .segs span.on{background:#1A5490}'
      + '.lbf-moq-strip .lbl{background:none;border:0;color:inherit;font:inherit;font-weight:700;cursor:pointer;padding:0;text-align:left;flex:1}'
      + '.lbf-moq-strip .fix{background:none;border:0;color:inherit;font:inherit;font-size:10px;font-weight:800;cursor:pointer;padding:0;text-decoration:underline;text-underline-offset:2px;flex-shrink:0}'
      + '.lbf-moq-iic{display:inline-flex;align-items:center;justify-content:center;width:15px;height:15px;border-radius:999px;border:1.5px solid currentColor;color:inherit;background:none;font-size:9.5px;font-weight:800;cursor:pointer;font-style:normal;flex-shrink:0;padding:0;font-family:inherit;line-height:1}'
      /* removal banner (floats just below the visible header edge; z-index kept
         BELOW the header's — JS refines both live while shown) */
      + '#lbf-moq-banner{position:fixed;left:10px;right:10px;background:#E6F4EA;color:#1B7B1B;border:1px solid #bfe3c4;border-radius:10px;padding:10px 13px;font-size:12px;font-weight:600;display:none;align-items:center;gap:10px;z-index:2;box-shadow:0 4px 14px rgba(0,0,0,.12);opacity:0;transform:translateY(-8px);transition:opacity .25s,transform .25s;line-height:1.35;max-width:560px;margin:0 auto}'
      + '#lbf-moq-banner.show{display:flex;opacity:1;transform:translateY(0)}'
      + '#lbf-moq-banner span{flex:1}'
      + '#lbf-moq-banner button{background:none;border:0;color:#1B7B1B;font-weight:800;cursor:pointer;text-decoration:underline;text-underline-offset:2px;flex-shrink:0;font-size:12px;font-family:inherit;padding:0}'
      /* why-popup (own copy of the lbf-tooltip pattern) */
      + '#lbf-moq-pop-bd{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:99988;display:none;opacity:0;transition:opacity .18s}'
      + '#lbf-moq-pop-bd.open{display:block;opacity:1}'
      + '#lbf-moq-pop{position:fixed;left:50%;top:50%;transform:translate(-50%,-50%) scale(.96);background:#fff;border-radius:12px;padding:20px 18px 18px;max-width:380px;width:calc(100% - 32px);box-shadow:0 16px 40px rgba(0,0,0,.22);z-index:99989;display:none;opacity:0;transition:opacity .2s,transform .2s}'
      + '#lbf-moq-pop.open{display:block;opacity:1;transform:translate(-50%,-50%) scale(1)}'
      + '#lbf-moq-pop h3{font-size:15px;font-weight:800;margin:0 24px 8px 0;color:#111;line-height:1.3}'
      + '#lbf-moq-pop p{font-size:12.5px;color:#444;line-height:1.55;margin:0 0 8px}'
      + '#lbf-moq-pop .lbf-moq-pop-x{position:absolute;top:8px;right:10px;background:none;border:none;cursor:pointer;font-size:20px;color:#999;line-height:1;padding:2px 6px}'
      + '#lbf-moq-pop .lbf-moq-btnrow{margin-top:12px}';
    var st = document.createElement('style');
    st.id = 'lbf-moq-css';
    st.textContent = css;
    document.head.appendChild(st);
  }

  /* ---------------- why-popup ---------------- */

  function ensurePopup() {
    var bd = document.getElementById('lbf-moq-pop-bd');
    if (bd) return;
    beginWrite();
    bd = document.createElement('div');
    bd.id = 'lbf-moq-pop-bd';
    var card = document.createElement('div');
    card.id = 'lbf-moq-pop';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.innerHTML =
      '<button type="button" class="lbf-moq-pop-x" data-lbf-act="close-pop" aria-label="Close">&times;</button>' +
      '<h3>❄️ Why does cold-chain delivery need min. 3 packs?</h3>' +
      '<p>Frozen Family Packs are shipped by <b>cold-chain in an insulated box with ice packs</b>.</p>' +
      '<p>With fewer than 3 packs, the box has <b>too little frozen volume</b> to stay cold for the journey, and the cold-chain delivery fee becomes less worthwhile for you.</p>' +
      '<p>Ready-To-Eat packs are room-temperature items, so they can check out separately with <b>no frozen minimum</b>.</p>' +
      '<div class="lbf-moq-btnrow" id="lbf-moq-pop-actions"></div>';
    document.body.appendChild(bd);
    document.body.appendChild(card);
    bd.addEventListener('click', closePopup);
    endWrite();
  }

  function openPopup() {
    ensurePopup();
    var st = computeState();
    var actions = document.getElementById('lbf-moq-pop-actions');
    var html = '';
    if (st.blocked) {
      var rte = rteInfo();
      var addPrimary = isAddPrimary(st);
      var btnAdd = '<button type="button" class="lbf-moq-btn ' + (addPrimary ? 'is-primary' : 'is-secondary') + '" data-lbf-act="add">Browse Frozen Best Sellers</button>';
      var btnRte = rte.count > 0
        ? '<button type="button" class="lbf-moq-btn ' + (addPrimary ? 'is-secondary' : 'is-primary') + '" data-lbf-act="remove">Checkout Ready-to-Eat first' + (rteFreeShipUnlocked(rte) ? '<small><span class="lbf-ship-free">West Msia Free Shipping</span></small>' : '') + '</button>'
        : '';
      html = rte.count > 0 ? (addPrimary ? btnAdd + btnRte : btnRte + btnAdd) : btnAdd;
    }
    if (actions) actions.innerHTML = html;
    document.getElementById('lbf-moq-pop-bd').className = 'open';
    document.getElementById('lbf-moq-pop').className = 'open';
  }

  function closePopup() {
    var bd = document.getElementById('lbf-moq-pop-bd');
    var card = document.getElementById('lbf-moq-pop');
    if (bd) bd.className = '';
    if (card) card.className = '';
  }

  /* ---------------- removal banner ---------------- */

  function ensureBanner() {
    var b = document.getElementById('lbf-moq-banner');
    if (b) return b;
    beginWrite();
    b = document.createElement('div');
    b.id = 'lbf-moq-banner';
    b.setAttribute('role', 'status');
    b.innerHTML = '<span></span><button type="button" data-lbf-act="undo">UNDO</button>';
    document.body.appendChild(b);
    endWrite();
    return b;
  }

  function headerEl() {
    return document.querySelector('.shopify-section-header-sticky')
      || document.querySelector('.section-header')
      || document.querySelector('sticky-header')
      || document.querySelector('.header-wrapper');
  }

  /* v1.1: Dawn's sticky header slides back in on scroll-up and used to overlay
     the banner. Track the header's live bottom edge every frame while the
     banner is visible (covers scroll, resize AND the header's own CSS slide
     transition), and keep the banner's z-index one below the header's so it
     can never cover navigation while still sitting above page content. */
  function positionBanner() {
    var b = document.getElementById('lbf-moq-banner');
    if (!b || !b.classList.contains('show')) return;
    var top = 8;
    var hdr = headerEl();
    if (hdr) {
      try {
        var r = hdr.getBoundingClientRect();
        if (r.height > 0 && r.bottom > 0) top = r.bottom + 8;
        var hz = parseInt(window.getComputedStyle(hdr).zIndex, 10);
        var z = String(isNaN(hz) ? 2 : Math.max(1, hz - 1));
        if (b.style.zIndex !== z) b.style.zIndex = z;
      } catch (e) {}
    }
    var t = Math.round(top) + 'px';
    if (b.style.top !== t) b.style.top = t;
  }

  var bannerRaf = null;
  function trackBanner() {
    positionBanner();
    var b = document.getElementById('lbf-moq-banner');
    if (b && b.classList.contains('show')) bannerRaf = requestAnimationFrame(trackBanner);
    else bannerRaf = null;
  }

  function showBanner(n) {
    var b = ensureBanner();
    b.querySelector('span').textContent =
      n + ' frozen dish' + (n > 1 ? 'es' : '') + ' removed. Your Ready-to-Eat order is ready to checkout';
    b.classList.add('show');
    positionBanner();
    if (!bannerRaf) bannerRaf = requestAnimationFrame(trackBanner);
    clearTimeout(bannerTimer);
    bannerTimer = setTimeout(hideBanner, 8000);
  }

  function hideBanner() {
    var b = document.getElementById('lbf-moq-banner');
    if (b) b.classList.remove('show');
    clearTimeout(bannerTimer);
  }

  /* ---------------- Dawn section re-render after cart mutations ---------------- */

  function notifyCartUpdate() {
    try { document.dispatchEvent(new CustomEvent('cart:update', { detail: { source: 'lbf-frozen-moq' } })); } catch (e) {}
    try {
      if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'lbf-frozen-moq' });
      }
    } catch (e) {}
  }

  function refreshSections(cb) {
    var ids = [];
    var itemsEl = itemsSection();
    var itemsId = itemsEl && itemsEl.getAttribute('data-id');
    if (itemsId) ids.push(itemsId);
    var fSec = footerSection();
    var footId = fSec && fSec.id ? fSec.id.replace(/^shopify-section-/, '') : null;
    if (footId && ids.indexOf(footId) === -1) ids.push(footId);
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) ids.push('cart-icon-bubble');
    if (!ids.length) { window.location.reload(); return; }
    fetch(root() + '?sections=' + ids.join(','), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        beginWrite();
        try {
          if (itemsId && sections[itemsId]) {
            var doc = new DOMParser().parseFromString(sections[itemsId], 'text/html');
            var src = doc.querySelector('.js-contents');
            var dst = itemsEl.querySelector('.js-contents');
            if (src && dst) dst.innerHTML = src.innerHTML;
            var wrap = itemsEl.closest('cart-items');
            var srcWrap = doc.querySelector('cart-items');
            if (wrap && srcWrap) wrap.className = srcWrap.className;
          }
          if (footId && sections[footId] && fSec) {
            var fdoc = new DOMParser().parseFromString(sections[footId], 'text/html');
            var fsrc = fdoc.querySelector('.js-contents');
            var fdst = fSec.querySelector('.js-contents');
            if (fsrc && fdst) fdst.innerHTML = fsrc.innerHTML;
            var fMainSrc = fdoc.getElementById('main-cart-footer');
            var fMainDst = document.getElementById('main-cart-footer');
            if (fMainSrc && fMainDst) fMainDst.className = fMainSrc.className;
          }
          if (bubble && sections['cart-icon-bubble']) {
            var bdoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
            var bsrc = bdoc.querySelector('.shopify-section') || bdoc.body;
            if (bsrc) bubble.innerHTML = bsrc.innerHTML;
          }
        } catch (e) {}
        endWrite();
        notifyCartUpdate();
        scheduleRender(30);
        if (cb) cb();
      })
      .catch(function () { window.location.reload(); });
  }

  /* ---------------- actions: remove / undo / add / quick-add ---------------- */

  function removeAllFrozen() {
    beacon('lbf_moq_path_click', { path: 'remove' });
    fetchCart().then(function (cart) {
      if (!cart) return;
      var updates = {};
      var backup = [];
      var n = 0;
      (cart.items || []).forEach(function (it) {
        var t = String(it.product_type || '').toLowerCase();
        if (t === 'frozen') {
          updates[it.key] = 0;
          backup.push({ id: it.variant_id, quantity: it.quantity });
          n += it.quantity;
        }
      });
      if (!backup.length) return;
      fetch(root() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates: updates,
          attributes: { lbf_moq_resolution: 'remove' }
        })
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          removedBackup = backup;
          cartCache = null;
          refreshSections(function () { showBanner(n); scrollToCheckout(); });
        })
        .catch(function () {});
    });
  }

  function undoRemove() {
    if (!removedBackup) { hideBanner(); return; }
    beacon('lbf_moq_path_click', { path: 'undo' });
    var items = removedBackup;
    removedBackup = null;
    hideBanner();
    fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json(); })
      .then(function () { cartCache = null; refreshSections(); })
      .catch(function () {});
  }

  function goAdd() {
    beacon('lbf_moq_path_click', { path: 'add' });
    markBrowseIntent();
    window.location.href = ADD_URL;
  }

  function quickAdd(variantId, btn) {
    if (btn) { btn.disabled = true; btn.textContent = '…'; }
    beacon('lbf_moq_path_click', { path: 'add', quick_add: true, variant_id: variantId });
    fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Unable to add frozen item');
        return r.json();
      })
      .then(function () { cartCache = null; return fetchCart(); })
      .then(function (cart) {
        if (!cart || frozenQtyFromCart(cart) < MOQ) return false;
        return setResolution('add').then(function (updatedCart) {
          cartCache = updatedCart;
          return true;
        }).catch(function () { return false; });
      })
      .then(function () { refreshSections(); })
      .catch(function () { if (btn) { btn.disabled = false; btn.textContent = 'ADD'; } });
  }

  /* ---------------- quick-add strip ---------------- */

  function frozenHandleList() {
    var out = [];
    var sec = document.getElementById('cart-recommendations');
    if (sec && sec.getAttribute('data-frozen-collections')) {
      sec.getAttribute('data-frozen-collections').split(',').forEach(function (h) {
        h = h.replace(/^\s+|\s+$/g, '');
        if (h) out.push(h);
      });
    }
    FROZEN_HANDLES.forEach(function (h) { if (out.indexOf(h) === -1) out.push(h); });
    return out;
  }

  function firstFrozenProductId() {
    var items = ((cartCache || {}).items) || [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].product_type || '').toLowerCase() === 'frozen') return items[i].product_id;
    }
    return null;
  }

  function productFromHandle(handle) {
    return fetch(root() + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function collectionBestSellerProducts(handle) {
    return fetch(root() + 'collections/' + encodeURIComponent(handle) + '?sort_by=best-selling', { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var seen = {};
        var handles = [];
        Array.prototype.slice.call(doc.querySelectorAll('a[href*="/products/"]')).forEach(function (a) {
          var m = String(a.getAttribute('href') || '').match(/\/products\/([a-z0-9-]+)/i);
          if (!m || seen[m[1]]) return;
          seen[m[1]] = 1;
          handles.push(m[1]);
        });
        handles = handles.slice(0, 24);
        return Promise.all(handles.map(productFromHandle));
      })
      .catch(function () { return []; });
  }

  /* Best-seller source of truth: use frozen-family-meals sorted by Shopify's
     best-selling collection view, then fetch each product JSON in that order. */
  function loadQuickPool() {
    if (quickPool || quickLoading) return;
    /* Cart journey 2026-09-26: the new cart page has its own frozen picks and hides this helper, so don't
       download ~18 frozen products on every cart load for a panel nobody sees. */
    if (document.documentElement.classList.contains('lbf-cj-cart')) return;
    quickLoading = true;
    var pool = [];
    var seen = {};
    function absorb(list) {
      (list || []).forEach(function (p) {
        if (!p || !p.id || seen[p.id]) return;
        /* collection products.json uses product_type; recommendations (.js format) uses type */
        if (String(p.product_type || p.type || '').toLowerCase() !== 'frozen') return;
        /* Cart journey P1 bug 1 (2026-09-26): a cc_solo_ok dish (Pork Knuckle) does not
           count towards the 3-dish minimum, so offering it here left the shopper blocked. */
        var ptags = (p.tags && p.tags.join ? p.tags : String(p.tags || '').split(',')).map(function (x) { return String(x).trim().toLowerCase(); });
        if (ptags.indexOf('cc_solo_ok') !== -1 || ptags.indexOf('free-gift') !== -1) return;
        seen[p.id] = 1;
        pool.push(p);
      });
    }
    function finish() {
      quickPool = pool;
      quickLoading = false;
      scheduleRender(0);
    }
    var handles = frozenHandleList();
    var idx = 0;
    function tryNext() {
      /* keep topping up until we comfortably cover the 8-card strip even after
         in-cart/sold-out exclusions (a seeded pool must not stop the walk
         early — some handles, e.g. frozen-series, are empty) */
      if (pool.length >= 12 || idx >= handles.length) {
        if (pool.length) finish();
        else fallback();
        return;
      }
      var h = handles[idx++];
      collectionBestSellerProducts(h)
        .then(function (products) {
          absorb(products);
          tryNext();
        })
        .catch(tryNext);
    }
    function fallback() {
      fetch(root() + 'products.json?limit=250', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) { absorb(j && j.products); finish(); })
        .catch(function () { finish(); });
    }
    tryNext();
  }

  /* USP metafield badges (pax/weight/time): the legacy JSON endpoints this file
     already uses (products.json / recommendations.json) don't carry metafields,
     so scrape the real Liquid-rendered .lbf-card-meta markup off the actual
     frozen collection page(s) once per page load and key it by product handle --
     keeps the cart's pills pixel/copy-identical to the collection page with zero
     extra Shopify API surface. Best-effort: no match just means no pills. */
  function loadUspMap(cb) {
    if (uspByHandle || uspLoading) { if (cb) cb(); return; }
    uspLoading = true;
    var handles = frozenHandleList();
    var idx = 0;
    var map = {};
    function absorb(html) {
      try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var links = doc.querySelectorAll('a[href*="/products/"]');
        Array.prototype.slice.call(links).forEach(function (a) {
          var m = String(a.getAttribute('href') || '').match(/\/products\/([a-z0-9-]+)/i);
          if (!m || map[m[1]]) return;
          var wrapper = a.closest('.card-wrapper') || a.closest('.card');
          var meta = wrapper && wrapper.querySelector('.lbf-card-meta');
          if (meta) map[m[1]] = meta.outerHTML;
        });
      } catch (e) {}
    }
    function finish() { uspByHandle = map; uspLoading = false; if (cb) cb(); }
    function tryNext() {
      if (Object.keys(map).length >= 12 || idx >= handles.length) { finish(); return; }
      var h = handles[idx++];
      fetch(root() + 'collections/' + encodeURIComponent(h), { headers: { Accept: 'text/html' } })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) { absorb(html); tryNext(); })
        .catch(tryNext);
    }
    tryNext();
  }

  /* S4/S5-v1: pick a single best-seller-first, in-stock, not-already-in-cart
     product+variant from the same pool the carousel uses, for the "single
     suggested pack, one-tap add" hero treatment when need===1. */
  function pickHeroProduct() {
    if (!quickPool || !quickPool.length) return null;
    var inCart = {};
    (((cartCache || {}).items) || []).forEach(function (it) { inCart[it.product_id] = 1; });
    for (var i = 0; i < quickPool.length; i++) {
      var p = quickPool[i];
      if (inCart[p.id] || p.available === false) continue;
      var vs = p.variants || [];
      var v = null;
      for (var j = 0; j < vs.length; j++) {
        if (vs[j].available !== false) { v = vs[j]; break; }
      }
      if (v) return { p: p, v: v };
    }
    return null;
  }

  function heroPickHTML(pick) {
    var p = pick.p, v = pick.v;
    var img = '';
    if (p.images && p.images.length) img = String(p.images[0].src || p.images[0] || '');
    else if (p.featured_image) img = String(p.featured_image);
    if (img) img += (img.indexOf('?') === -1 ? '?' : '&') + 'width=160';
    var curCents = priceToCents(v.price);
    var cmpCents = v.compare_at_price ? priceToCents(v.compare_at_price) : 0;
    var priceHTML = cmpCents > curCents
      ? '<span class="orig">' + fmt(cmpCents) + '</span><span class="cur">' + fmt(curCents) + '</span>'
      : fmt(curCents);
    var uspHtml = (uspByHandle && p.handle && uspByHandle[p.handle]) || '';
    return '<div class="lbf-moq-hero-pick">'
      + (img ? '<img loading="lazy" src="' + escapeHTML(img) + '" alt="' + escapeHTML(p.title) + '">' : '')
      + '<div class="lbf-moq-hero-info">'
      + uspHtml
      + '<div class="t">' + escapeHTML(p.title) + '</div>'
      + '<div class="p">' + priceHTML + '</div>'
      + '</div>'
      + '<button type="button" class="lbf-moq-hero-add" data-lbf-quickadd="' + v.id + '">ADD</button>'
      + '</div>';
  }

  function stripHTML(excludeId) {
    if (!quickPool) { loadQuickPool(); return ''; }
    if (!quickPool.length) return '';
    var inCart = {};
    (((cartCache || {}).items) || []).forEach(function (it) { inCart[it.product_id] = 1; });
    var rows = '';
    var shown = 0;
    for (var i = 0; i < quickPool.length && shown < 6; i++) {
      var p = quickPool[i];
      if (inCart[p.id]) continue;
      if (excludeId && p.id === excludeId) continue; /* already offered as the single hero pick */
      if (p.available === false) continue; /* sold out (.js/recommendations format) */
      var v = null;
      var vs = p.variants || [];
      for (var j = 0; j < vs.length; j++) {
        if (vs[j].available !== false) { v = vs[j]; break; }
      }
      if (!v) continue; /* no purchasable variant -> skip sold-out product */
      var img = '';
      if (p.images && p.images.length) img = String(p.images[0].src || p.images[0] || '');
      else if (p.featured_image) img = String(p.featured_image);
      if (img) img += (img.indexOf('?') === -1 ? '?' : '&') + 'width=160';
      var curCents = priceToCents(v.price);
      var cmpCents = v.compare_at_price ? priceToCents(v.compare_at_price) : 0;
      var priceHTML = cmpCents > curCents
        ? '<span class="orig">' + fmt(cmpCents) + '</span><span class="cur">' + fmt(curCents) + '</span>'
        : fmt(curCents);
      var uspHtml = (uspByHandle && p.handle && uspByHandle[p.handle]) || '';
      rows += '<div class="lbf-moq-row">'
        + (img ? '<img loading="lazy" src="' + escapeHTML(img) + '" alt="' + escapeHTML(p.title) + '">' : '')
        + '<div class="lbf-moq-row-info">'
        + uspHtml
        + '<div class="t">' + escapeHTML(p.title) + '</div>'
        + '<div class="p">' + priceHTML + '</div>'
        + '</div>'
        + '<button type="button" data-lbf-quickadd="' + v.id + '">ADD</button>'
        + '</div>';
      shown++;
    }
    if (!rows) return '';
    return '<div class="lbf-moq-carousel" data-lbf-carousel>'
      + '<button type="button" class="lbf-moq-arrow lbf-moq-arrow--prev" data-lbf-scroll="prev" aria-label="Previous frozen dishes" hidden><svg viewBox="0 0 10 16" aria-hidden="true" focusable="false"><path d="M8.5 1 2 8l6.5 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button>'
      + '<div class="lbf-moq-rows">' + rows + '</div>'
      + '<button type="button" class="lbf-moq-arrow lbf-moq-arrow--next" data-lbf-scroll="next" aria-label="More frozen dishes"><svg viewBox="0 0 10 16" aria-hidden="true" focusable="false"><path d="M1.5 1 8 8l-6.5 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button>'
      + '</div>';
  }

  /* ---------------- helper card ---------------- */

  function helperHTML(st) {
    var why = '<button type="button" class="lbf-moq-why" data-lbf-act="why">Why does cold-chain delivery need min. 3 packs?</button>';
    var rte = rteInfo();
    var mixed = rte.count > 0;
    var addPrimary = isAddPrimary(st);

    if (mixed) {
      /* S4/S5-v1 (playbook): need===1 is the easiest ask + highest expected
         margin in the system -> strongest visual treatment in the card,
         a single suggested pack with a one-tap add (not the generic
         multi-pack browse card used for need===2). */
      if (addPrimary && st.need === 1) {
        var heroPick = pickHeroProduct();
        var heroFlipSub = '<p class="lbf-moq-sub">Add this pack and your order is ready to checkout.</p>';
        var heroMain = heroPick
          ? heroPickHTML(heroPick)
          : '<button type="button" class="lbf-moq-btn is-primary" data-lbf-act="add">Browse Frozen Best Sellers</button>';
        var altStrip = stripHTML(heroPick ? heroPick.p.id : null);
        return '<div class="lbf-moq-card lbf-moq-hero">'
          + '<h3>Just 1 more pack to go!</h3>'
          + heroFlipSub
          + heroMain
          + (altStrip ? '<div class="lbf-moq-hero-alt"><p class="lbf-moq-hero-alt-label">Or choose another pack:</p>' + altStrip + '</div>' : '')
          + why + '</div>';
      }

      /* Carl 2026-07-09 (round 3): the button used to repeat "Add N more
         frozen dishes", duplicating the <h3> above it -- collapsed to one
         label, "Browse Frozen Best Sellers", in every state; the free-ship
         flip nudge (when adding would cross a threshold) now shows as green
         subtext under that same label instead of replacing it. */
      var btnAdd = '<button type="button" class="lbf-moq-btn ' + (addPrimary ? 'is-primary' : 'is-secondary') + '" data-lbf-act="add">'
        + 'Browse Frozen Best Sellers'
        + '</button>';
      /* Carl 2026-07-09 (round 3): simplified to one static line -- "Remove
         Frozen Dishes" -- describing what the button DOES, instead of a
         dynamic "N items · RMx.xx · frozen removed" / free-ship readout.
         The cart's own shipping-line summary is the single source of truth
         for free-shipping status; this button no longer duplicates that math. */
      var btnRte = '<button type="button" class="lbf-moq-btn ' + (addPrimary ? 'is-secondary' : 'is-primary') + '" data-lbf-act="remove">'
        + 'Checkout Ready-to-Eat first'
        + (rteFreeShipUnlocked(rte) ? '<small><span class="lbf-ship-free">West Msia Free Shipping</span></small>' : '')
        + '</button>';
      if (addPrimary) {
        return '<div class="lbf-moq-card">'
          + '<h3>Just ' + st.need + ' more frozen dishes to complete your order</h3>'
          + stripHTML()
          + '<div class="lbf-moq-btnrow">' + btnAdd + '</div>'
          + why + '</div>';
      }
      return '<div class="lbf-moq-card">'
        + '<h3>Your Ready-to-Eat items can checkout now</h3>'
        + '<div class="lbf-moq-btnrow">' + btnRte + '</div>'
        + why + '</div>';
    }
    /* frozen-only cart: adding is the only path (no remove rescue) */
    return '<div class="lbf-moq-card">'
      + '<h3>Add ' + st.need + ' more frozen dish' + (st.need > 1 ? 'es' : '') + ' for cold-chain delivery</h3>'
      + stripHTML()
      + '<button type="button" class="lbf-moq-btn is-primary lbf-moq-browse" data-lbf-act="add">Browse Frozen Best Sellers</button>'
      + why + '</div>';
  }

  function fillHelper(st) {
    var slot = document.querySelector('.lbf-moq-helper-slot td');
    if (!slot) return;
    if (!st.blocked) {
      if (slot.innerHTML !== '') { beginWrite(); slot.innerHTML = ''; endWrite(); }
      slot.__lbfHtml = '';
      return;
    }
    if (!cartCache) {
      if (!cartFetching) fetchCart().then(function () { scheduleRender(0); });
      return;
    }
    if (!uspByHandle && !uspLoading) loadUspMap(function () { scheduleRender(0); });
    var html = helperHTML(st);
    /* compare against the last written string, not innerHTML — the carousel JS
       mutates [hidden] attrs after render, which would make innerHTML never
       match and cause endless re-writes (losing scroll position) */
    if (slot.__lbfHtml !== html) {
      beginWrite();
      slot.innerHTML = html;
      slot.__lbfHtml = html;
      endWrite();
    }
    updateAllCarouselArrows();
    /* blocker-view beacon: once per state change */
    var rte = rteInfo();
    var primaryPath = primaryPathFor(st);
    var key = st.fq + '|' + primaryPath;
    if (key !== lastBeaconKey) {
      lastBeaconKey = key;
      beacon('lbf_moq_blocker_view', {
        frozen_qty: st.fq,
        cart_value: cartCache.total_price || 0,
        primary_path: primaryPath
      });
    }
  }

  function updateCarouselArrows(car) {
    var rows = car && car.querySelector('.lbf-moq-rows');
    if (!rows) return;
    var prev = car.querySelector('[data-lbf-scroll="prev"]');
    var next = car.querySelector('[data-lbf-scroll="next"]');
    var max = Math.max(0, rows.scrollWidth - rows.clientWidth - 2);
    var left = rows.scrollLeft || 0;
    if (prev) prev.hidden = max <= 0 || left <= 2;
    if (next) next.hidden = max <= 0 || left >= max;
  }

  function updateAllCarouselArrows() {
    Array.prototype.slice.call(document.querySelectorAll('[data-lbf-carousel]')).forEach(updateCarouselArrows);
  }

  /* ---------------- header (frozen section) ---------------- */

  function updateHeader(st) {
    var prog = document.querySelector('[data-lbf-fz-progress]');
    var counter = document.querySelector('[data-lbf-fz-counter]');
    if (prog) {
      if (st.blocked) {
        var h = '';
        for (var n = 1; n <= MOQ; n++) h += '<span' + (st.fq >= n ? ' class="on"' : '') + '></span>';
        beginWrite();
        if (prog.innerHTML !== h) prog.innerHTML = h;
        prog.hidden = false;
        endWrite();
      } else if (!prog.hidden) {
        beginWrite();
        prog.hidden = true;
        endWrite();
      }
    }
    if (counter) {
      if (st.blocked) {
        var txt = st.fq + ' of ' + MOQ + ' minimum';
        beginWrite();
        if (counter.textContent !== txt) counter.textContent = txt;
        counter.hidden = false;
        endWrite();
      } else if (!counter.hidden) {
        beginWrite();
        counter.hidden = true;
        endWrite();
      }
    }
  }

  /* ---------------- footer: gate + strip ---------------- */

  function setGate(st) {
    var fSec = footerSection();
    if (fSec) fSec.classList.toggle('lbf-moq-blocked', st.blocked);
    getCheckoutButtons().forEach(function (b) {
      var isLink = b.tagName.toLowerCase() === 'a';
      var combinedBlocked = st.blocked || b.getAttribute('data-lbf-shipping-blocked') === '1';
      if (!isLink) b.disabled = combinedBlocked;
      b.setAttribute('aria-disabled', combinedBlocked ? 'true' : 'false');
      b.style.opacity = combinedBlocked ? '0.5' : '';
      b.style.cursor = combinedBlocked ? 'not-allowed' : '';
    });
  }

  function ensureStrip(st) {
    var fSec = footerSection();
    if (!fSec) return;
    var w = fSec.querySelector('.lbf-moq-strip');
    if (!st.blocked) {
      if (w) { beginWrite(); w.parentNode.removeChild(w); endWrite(); }
      return;
    }
    if (!w) {
      /* stable wrapper OUTSIDE .js-contents (same pattern as cart-footer-fix
         ensureShippingWrapper) so Dawn AJAX re-renders don't wipe it */
      var ctas = fSec.querySelector('.cart__ctas');
      if (!ctas) return;
      beginWrite();
      w = document.createElement('div');
      w.className = 'lbf-moq-strip';
      ctas.parentNode.insertBefore(w, ctas);
      endWrite();
    }
    var segs = '';
    for (var n = 1; n <= MOQ; n++) segs += '<span' + (st.fq >= n ? ' class="on"' : '') + '></span>';
    /* i-icon leads (replaces the decorative snowflake — it's the same "why"
       trigger, no need for two icons) -> label -> progress -> resolve action */
    var html = '<button type="button" class="lbf-moq-iic" data-lbf-act="why" aria-label="Why 3 packs?">i</button>'
      + '<button type="button" class="lbl" data-lbf-act="fix">Frozen: ' + st.fq + ' of ' + MOQ + ' minimum</button>'
      + '<span class="segs">' + segs + '</span>'
      + '<button type="button" class="fix" data-lbf-act="fix">Resolve ↑</button>';
    if (w.innerHTML !== html) { beginWrite(); w.innerHTML = html; endWrite(); }
  }

  function scrollToHelper() {
    var h = document.querySelector('.lbf-cart-sec--frozen') || document.querySelector('.lbf-moq-card') || document.querySelector('.lbf-moq-helper-slot');
    if (h && h.scrollIntoView) h.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function scrollToCheckout() {
    var target = getCheckoutButtons()[0] || footerSection();
    if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /* ---------------- render ---------------- */

  function render() {
    var st = computeState();
    currentBlocked = st.blocked;
    if (!st.blocked) lastBeaconKey = '';
    if (st.blocked) hideBanner(); /* stale confirmation never coexists with a live block */
    updateHeader(st);
    setGate(st);
    ensureStrip(st);
    fillHelper(st);
    publishPrimaryPath(st);
  }

  function scheduleRender(ms) {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(render, ms == null ? 250 : ms);
  }

  /* ---------------- wiring ---------------- */

  function onDocClick(e) {
    var t = e.target;
    var scrollerBtn = t && t.closest ? t.closest('[data-lbf-scroll]') : null;
    if (scrollerBtn) {
      var car = scrollerBtn.closest('[data-lbf-carousel]');
      var rows = car && car.querySelector('.lbf-moq-rows');
      if (rows) {
        var dir = scrollerBtn.getAttribute('data-lbf-scroll') === 'prev' ? -1 : 1;
        var firstRow = rows.querySelector('.lbf-moq-row');
        var amount = firstRow ? firstRow.getBoundingClientRect().width + 8 : rows.clientWidth * 0.75;
        try { rows.scrollBy({ left: dir * amount, behavior: 'smooth' }); }
        catch (err) { rows.scrollLeft += dir * amount; }
        setTimeout(function () { updateCarouselArrows(car); }, 260);
      }
      return;
    }
    var actEl = t && t.closest ? t.closest('[data-lbf-act]') : null;
    if (actEl) {
      var act = actEl.getAttribute('data-lbf-act');
      if (act === 'add') { closePopup(); goAdd(); return; }
      if (act === 'remove') { closePopup(); removeAllFrozen(); return; }
      if (act === 'why') { openPopup(); return; }
      if (act === 'fix') { scrollToHelper(); return; }
      if (act === 'undo') { undoRemove(); return; }
      if (act === 'close-pop') { closePopup(); return; }
    }
    var qa = t && t.closest ? t.closest('[data-lbf-quickadd]') : null;
    if (qa) { quickAdd(qa.getAttribute('data-lbf-quickadd'), qa); }
  }

  function onGuardClick(e) {
    if (!currentBlocked) return;
    var btns = getCheckoutButtons();
    var hit = null;
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (e.target === b || (b.contains && b.contains(e.target))) { hit = b; break; }
    }
    if (!hit) return;
    e.preventDefault();
    e.stopPropagation();
    scrollToHelper();
  }

  function init() {
    if (!itemsSection() && !document.querySelector('cart-items')) return;
    injectCSS();
    document.addEventListener('click', onDocClick);
    document.addEventListener('click', onGuardClick, true); /* capture-phase gate */
    document.addEventListener('change', function (e) {
      if (e.target && e.target.matches && e.target.matches('input[name^="updates"], input.quantity__input')) {
        cartCache = null;
        scheduleRender(250);
      }
    });
    ['cart:update', 'cart:updated', 'cart:refresh', 'cart:item-added', 'cart:item-removed', 'cart-update'].forEach(function (ev) {
      document.addEventListener(ev, function () { cartCache = null; scheduleRender(120); });
    });
    document.addEventListener('lbf:shipping-state-change', function () { scheduleRender(80); });
    window.addEventListener('storage', function (e) {
      if (e && e.key === 'lbf-ship-state') scheduleRender(80);
    });
    try {
      if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (payload) {
          if (!useCartPayload(payload)) {
            cartCache = null;
            scheduleRender(180);
          }
        });
      }
    } catch (e) {}
    if (!window.__lbfMoqFetchPatched) {
      window.__lbfMoqFetchPatched = true;
      var nativeFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        var p = nativeFetch.apply(this, arguments);
        if (/\/cart\/(change|update|clear)/.test(url)) {
          p.then(function (res) {
            try {
              res.clone().json().then(function (cart) {
                if (!useCartPayload(cart)) {
                  cartCache = null;
                  scheduleRender(180);
                }
              }).catch(function () {
                cartCache = null;
                scheduleRender(180);
              });
            } catch (e) {
              cartCache = null;
              scheduleRender(180);
            }
          }).catch(function () {});
        }
        return p;
      };
    }
    var obsRoot = itemsSection() || document.querySelector('main') || document.body;
    try {
      new MutationObserver(function () {
        if (writing) return;
        cartCache = cartCache; /* keep money cache; DOM drives MOQ truth */
        scheduleRender(250);
      }).observe(obsRoot, { childList: true, subtree: true });
      var fSec = footerSection();
      if (fSec) {
        new MutationObserver(function () {
          if (writing) return;
          scheduleRender(250);
        }).observe(fSec, { childList: true, subtree: true });
      }
    } catch (e) {}
    window.addEventListener('pageshow', function () {
      cartCache = null;
      fetchCart().then(function (cart) {
        return consumeBrowseIntent(cart);
      }).then(function () { scheduleRender(80); });
    });
    /* banner follows the header edge on scroll/resize too (the rAF loop covers
       the header's own slide transition; these cover throttled-rAF situations) */
    window.addEventListener('scroll', positionBanner, { passive: true });
    window.addEventListener('resize', function () {
      positionBanner();
      updateAllCarouselArrows();
    });
    document.addEventListener('scroll', updateAllCarouselArrows, true);
    fetchCart().then(function (cart) {
      return consumeBrowseIntent(cart);
    }).then(function () { render(); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
